import type { AmsFundingProgram, PublicFundingProgramStatus } from "@/app/appointments/booking-season/bookingSeasonApi"
import { defaultMessage, programLabel } from "@/app/appointments/funding-switches/lib/fundingSwitchForm"

/** A program a public booking can be made for. SHEAP is never inferred from a booking. */
export type HouseholdProgram = Exclude<AmsFundingProgram, "Sheap">

/**
 * The programs a household could be booked for, from its energy answers: a mirror of the server's
 * `AmsProgramMatrix.ProgramsFor`. Every household may apply for LIHEAP; a PSE customer may also apply
 * for PSE HELP for the service it named, or both services when it named both or none we recognise.
 */
export function programsFor(energySource: string | undefined, pseService: string | undefined): HouseholdProgram[] {
  if (!matches(energySource, "PSE")) return ["Liheap"]
  if (matches(pseService, "Gas")) return ["PseHelpGas", "Liheap"]
  if (matches(pseService, "Electric")) return ["PseHelpElectric", "Liheap"]
  return ["PseHelpGas", "PseHelpElectric", "Liheap"]
}

// NEEDS HUMAN DECISION (ams-hw9): wording.
export const PSE_HELP_ALREADY_ASSIGNED_REASON =
  "PSE HELP has already been assigned to your household this program year; LIHEAP may still be available."

/** Mirrors the server's `IncomeGuidelineFilter.AlreadyReceivedReason`. NEEDS HUMAN DECISION (ams-hw9): wording. */
export const ALREADY_RECEIVED_REASON = "Already received this program year."

/**
 * One program line on the household step (AB#607/AB#595 amendment). `status` is what the line shows:
 * already received wins over a switch, as on the server. `switchedOff` is the switch alone, which is
 * what the server's switch refusal asks.
 */
export interface HouseholdProgramLine {
  program: HouseholdProgram
  label: string
  status: "offered" | "switchedOff" | "alreadyReceived"
  switchedOff: boolean
  /** Why the program is not offered, or null while it is. */
  reason: string | null
}

/**
 * The household step's program lines for the chosen energy answers, from the public season payload's
 * `programs` (AB#607) and the step-2 program check's `receivedPrograms` ("Pse", "Liheap"; AB#595).
 */
export function householdProgramLines(
  energySource: string | undefined,
  pseService: string | undefined,
  programs: readonly PublicFundingProgramStatus[] | undefined,
  receivedPrograms: readonly string[],
): HouseholdProgramLine[] {
  return programsFor(energySource, pseService).map((program) => {
    const status = programs?.find((p) => p.program === program)
    const switchedOff = status?.isEnabled === false
    const label = programLabel(program)
    if (program === "Liheap" ? receivedPrograms.includes("Liheap") : receivedPrograms.includes("Pse")) {
      const reason = program === "Liheap" ? ALREADY_RECEIVED_REASON : PSE_HELP_ALREADY_ASSIGNED_REASON
      return { program, label, status: "alreadyReceived", switchedOff, reason }
    }
    if (switchedOff) {
      return { program, label, status: "switchedOff", switchedOff, reason: status?.message || defaultMessage(program) }
    }
    return { program, label, status: "offered", switchedOff, reason: null }
  })
}

/**
 * Whether every program the household could be booked for is switched off: the server's switch
 * refusal (`AmsProgramMatrix.SwitchedOffCandidates`), so the step does not continue to a booking the
 * server would refuse. Already received programs are not asked here; every one received is the
 * step-2 program check's own dead end.
 */
export function everyProgramSwitchedOff(lines: readonly HouseholdProgramLine[]): boolean {
  return lines.length > 0 && lines.every((line) => line.switchedOff)
}

function matches(value: string | undefined, expected: string): boolean {
  return (value ?? "").trim().toLowerCase() === expected.toLowerCase()
}
